import { FileInput } from "@/common/components/form/FileInput";
import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import {
  attachBilletFileInSchedulePayment,
  getPurchaseOrderById,
  updatePurchaseOrder,
  uploadFilePresignedUrl,
} from "@/common/services/purchaseOrder.api";
import { IPurchaseOrder } from "@/types/purchaseOrder.types";
import { Modal } from "flowbite-react";
import { FormikProvider, useFormik } from "formik";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ModalEditBilletDate } from "./ModalEditBilletDate";

type Props = {
  onClose: (data) => void;
};

function ModalEditBilletFiles(props: Props) {
  const { notifySuccess, notifyError } = useToastContext();
  const [purchase, setPurchase] = useState<IPurchaseOrder>();
  const { purchaseOrderId } = useParams();
  const [showModal, setShowModal] = useState(true);

  useEffect(() => {
    async function load() {
      if (!purchaseOrderId) return;
      const res = await getPurchaseOrderById(purchaseOrderId);
      setPurchase(res.data);
    }
    load();
  }, [props, purchaseOrderId]);

  interface FormValues {
    filesNF: File[];
    filesBillet: File[];
    filesPaymentProof: File[];
  }

  const initialValues: FormValues = {
    filesNF: [],
    filesBillet: [],
    filesPaymentProof: [],
  };

  function fileArrayToFileList(files: File[]): FileList {
    const dataTransfer = new DataTransfer();
    Array.from(files).forEach((file) => dataTransfer.items.add(file));
    return dataTransfer.files;
  }


	const formik = useFormik({
		initialValues,
		onSubmit: async (data) => {
			if (!purchase) return;

			const fileNamesNfe = Array.from(data.filesNF).map((f) => f.name).join(",");
			const fileNamesBillet = Array.from(data.filesBillet).map((f) => f.name).join(",");
			const fileNamesPaymentProof = Array.from(data.filesPaymentProof).map((f) => f.name).join(",");

			const appendNames = (existing?: string | FileList | null, newNames?: string) => {
				const e = ((existing as string) ?? "").trim();
				const n = (newNames ?? "").trim();
				if (!n && !e) return ""; 
				if (!n) return e; 
				if (!e) return n;
				return `${e},${n}`;
			};

			const updatedFinancial = {
				...purchase.financial,
				nfFiles: appendNames(purchase?.financial?.nfFiles, fileNamesNfe),
				billetFiles: appendNames(purchase?.financial?.billetFiles, fileNamesBillet),
				paymentProofFiles: appendNames(
					purchase?.financial?.paymentProofFiles,
					fileNamesPaymentProof
				),
			};

			const resultSendFiles = await attachBilletFileInSchedulePayment(
				{
					...purchase,
					financial: updatedFinancial,
					code: `${purchase.code}`,
				},
				fileArrayToFileList([
					...data.filesNF,
					...data.filesBillet,
					...data.filesPaymentProof,
				]),
				true
			);

			if (!resultSendFiles.ok) {
				notifyError("Erro ao enviar arquivos");
				return;
			}

			if (resultSendFiles.data.isPresigned) {
				for (const file of [
					...data.filesNF,
					...data.filesBillet,
					...data.filesPaymentProof,
				]) {
					const url = resultSendFiles.data.urls.find(
						(u) => file.name === u.name
					);
					if (!url) {
						notifyError("Erro ao enviar arquivos");
						return;
					}
					try {
						const { ok } = await uploadFilePresignedUrl(file, url.url);

						if (ok) {
							notifySuccess("Arquivos enviados com sucesso");
						} else {
							notifyError("Erro ao fazer upload de arquivos");
						}
					} catch {
						notifyError("Erro ao fazer upload de arquivos");
					}

				}
			}
			props.onClose({
				...purchase,
				financial: updatedFinancial,
			});
		},
	});


  const handleAddBilletFile = (file) => {
    if (!file) return;
    setShowModal(true);
  };


	const handleConfirmBilletData = async (values) => {
		if (!purchase) return;

		const updatedInstallments = values.parcels.map((parcel, index) => ({
			installmentNumber: index + 1,
			value: Number(parcel.amount),
			dueDate: parcel.date,
		}));

		props.onClose({
			...purchase,
			financial: {
				...purchase.financial,
				installment: updatedInstallments,
			},
		});

		if (purchase._id) {
			const result = await updatePurchaseOrder({
				...purchase, 
				financial: {
					...purchase.financial, 
					instalment: updatedInstallments
				}
			});
			if (!result.ok && result.message) {
				notifyError(result.message);
				return;
			}
		}

		setShowModal(false);
	};


  return (
    <>
      {formik.values.filesBillet.length > 0 && showModal && (
        <ModalEditBilletDate
          totalValue={purchase?.financial?.value}
          onClose={() => setShowModal(false)}
          onConfirm={handleConfirmBilletData}
        />
      )}

      <Modal onClose={() => props.onClose(purchase)} show={true}>
        <Modal.Header>
          <h2 className="text-xl font-semibold text-black">
            Adicionar Arquivos
          </h2>
        </Modal.Header>

        <Modal.Body>
          <FormikProvider value={formik}>
            <div className="space-y-6">
              <div className="p-4 border rounded-lg bg-gray-50">
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Anexar arquivos da NFe
                </h3>
                <FileInput
                  name="filesNF"
                  nameFile="Selecionar arquivo(s) NFe"
                />
              </div>

              <div className="p-4 border rounded-lg bg-gray-50">
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Anexar arquivos do Boleto
                </h3>

                <div className="flex gap-4">
                  <FileInput
                    name="filesBillet"
                    nameFile="Selecionar arquivo(s) Boleto"
                    onChange={(e) => handleAddBilletFile(e)}
                  />
                  <button className="mb-2" onClick={() => setShowModal(true)}>
                    Definir número de parcelas
                  </button>
                </div>
              </div>
              <div className="p-4 border rounded-lg bg-gray-50">
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Anexar Comprovante de Pagamento
                </h3>
                <div className="flex gap-4">
                  <FileInput
                    name="filesPaymentProof"
                    nameFile="Selecionar Comprovante(s)"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t">
              <ButtonWithLoader
                onClick={formik.handleSubmit}
                isLoading={formik.isSubmitting} // Adicione esta linha
              >
                Enviar Arquivos
              </ButtonWithLoader>
            </div>
          </FormikProvider>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default ModalEditBilletFiles;
